// DocumentView.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { resolveFileUrl } from 'services/storage/StorageAccessService';

// Utilities
import { isPdfDocumentNode } from 'utilities/fileMime';

// Components
import MediaDownloadFallback from 'node/media/MediaDownloadFallback';

// CSS
import './DocumentView.css';

// Icons
import DocumentIcon from 'svg/DocumentIcon';

/**
 * DocumentView
 *
 * PDFs use the native browser viewer. Other document types (xlsx, docx, …)
 * fall back to a download affordance — third-party document viewers are too
 * unreliable with signed storage URLs.
 *
 * @param {object} node - The document node to display.
 *
 * @returns {ReactNode} The rendered document feed node or null if invalid input.
 */
const DocumentView = ({ node }) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [resolvedRemoteUrl, setResolvedRemoteUrl] = useState(null);

    const isBlob = node?.url && node.url.startsWith('blob:');
    const isPdf = useMemo(() => isPdfDocumentNode(node), [node]);

    // Prefer schema-secured document URL (merged from `_nodes/{id}/secured/main`),
    // but fall back to older fields for legacy documents.
    const remoteUrl =
        node?.data_document?.securedUrl ||
        node?.data_document?.url ||
        node?.url ||
        null;

    // ----- EFFECTS ----- //

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!isPdf) {
                if (!cancelled) setResolvedRemoteUrl(null);
                return;
            }

            if (!remoteUrl || isBlob) {
                if (!cancelled) setResolvedRemoteUrl(remoteUrl || null);
                return;
            }

            const url = await resolveFileUrl({
                nodeKey: node?.key || null,
                url: remoteUrl,
                storagePath: node?.storagePath || node?.data_document?.storagePath || null,
            });
            if (cancelled) return;
            setResolvedRemoteUrl(url || null);
        })();

        return () => {
            cancelled = true;
        };
    }, [node, remoteUrl, isBlob, isPdf]);

    const viewerSrc = useMemo(() => {
        if (!isPdf || !resolvedRemoteUrl) return null;
        // Native browser PDF viewer: fit page width (better in narrow Peek).
        return `${resolvedRemoteUrl}#view=FitH&pagemode=none`;
    }, [resolvedRemoteUrl, isPdf]);

    /* ----- EARLY RETURN ----- */

    if (!node) return null;

    // Non-PDF documents: download-first (no Google Viewer iframe).
    if (!isPdf) {
        return <MediaDownloadFallback node={node} />;
    }

    // ----- RENDER ----- //

    return (
        <div
          className={[
            isBlob ? 'document-view-container-local' : 'document-view-container',
            !isBlob ? 'document-view-container--pdf' : '',
          ].filter(Boolean).join(' ')}
          style={isBlob ? { backgroundColor: theme.panel } : undefined}
        >

            {isBlob ? (
                <div className="document-local-preview">
                    <DocumentIcon
                      color={theme.foregroundMuted}
                      height={80}
                      width={80}
                    />

                    {/* DOCUMENT FILENAME */}
                    <div
                      className="document-filename"
                    >
                        {node.fileName || node?.data_document?.filename || 'Document'}
                    </div>
                </div>
            ) : remoteUrl ? (
                <>
                    {viewerSrc && (
                        <iframe
                          className="document-view-frame"
                          src={viewerSrc}
                          title="PDF Viewer"
                        />
                    )}
                </>
            ) : (
                <div className="document-placeholder">
                    <h3>{node.title || 'Document'}</h3>
                    {node.fileName && <p>File: {node.fileName}</p>}
                    <p>No document URL available</p>
                </div>
            )}
        </div>
    );
};

// Dev Tools
DocumentView.displayName = 'DocumentView';

export default DocumentView;
